/**
 * The lights one canvas item takes, as Godot lists them per item (`renderer_canvas_render_rd.cpp:
 * 2366-2385`). Items with the same list read the same accumulation buffer, which applies every
 * light on the list in Godot's order, so blends that do not commute (MIX) stay exact.
 */
/*
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 */
import { MAX_LIGHTS_PER_ITEM } from '../../godot/rendering/rendering.js';
import { lightReachesItem } from './lightCullKey.js';
import { rect2Intersects } from '../../godot/math/rect2.js';
import { perObject } from '../../utils/perObject.js';
/** The positional lights an item takes, `renderer_canvas_render_rd.cpp:2380`. */
export const MAX_POSITIONAL_LIGHTS_PER_ITEM = MAX_LIGHTS_PER_ITEM - 1;
/** The ordinals of the lights `rankOf` ranks, in list order: rank, then ordinal. */
function listOrder(lights, rankOf) {
    const listed = [];
    for (const [ordinal, light] of lights) {
        const rank = rankOf(light);
        if (rank !== null)
            listed.push({ ordinal, rank });
    }
    listed.sort((a, b) => a.rank - b.rank || a.ordinal - b.ordinal);
    return listed.map((light) => light.ordinal);
}
/** The positional lights in list order: sequence, then ordinal. */
export function positionalListOrder(lights) {
    return listOrder(lights, (light) => light.sequence);
}
/** The directional lights in list order: slot, then ordinal. */
export function directionalListOrder(lights) {
    return listOrder(lights, (light) => (light.sequence === null ? (light.slot ?? 0) : null));
}
/**
 * The positional lights of `positionalOrder` that pass the cull test at `item`, in list order.
 * More than `MAX_POSITIONAL_LIGHTS_PER_ITEM` crowds the placement, so each item's rect picks.
 */
export function positionalCandidates(lights, positionalOrder, item) {
    return positionalOrder.filter((ordinal) => {
        const { reach } = lights.get(ordinal);
        return lightReachesItem(reach, item.lightMask, item.z, item.layer);
    });
}
/**
 * Godot's per-item loop (`renderer_canvas_render_rd.cpp:2366-2385`) takes the first 15 of an item's
 * `positionalCandidates` whose rect meets the item's. A light that misses adds nothing at the
 * item's pixels, so the list keeps it and drops only the lights that meet the item past the 15th:
 * items then share lists. Null while none drops, for the placement's own list. A light with no
 * rect in `lightRects` draws nothing, so it meets no item.
 */
export function itemPositionalLights(candidates, itemRect, lightRects) {
    const meeting = candidates.filter((ordinal) => {
        const lightRect = lightRects.get(ordinal);
        return lightRect !== undefined && rect2Intersects(itemRect, lightRect);
    });
    if (meeting.length <= MAX_POSITIONAL_LIGHTS_PER_ITEM)
        return null;
    const dropped = new Set(meeting.slice(MAX_POSITIONAL_LIGHTS_PER_ITEM));
    return candidates.filter((ordinal) => !dropped.has(ordinal));
}
/** Whether `light` is on `item`'s list: it passes the cull test and, if the list is capped, the item takes it. */
function itemTakesLight(light, ordinal, item, taken) {
    if (!lightReachesItem(light.reach, item.lightMask, item.z, item.layer))
        return false;
    return taken === null || taken.has(ordinal);
}
/** The lights on `item`'s list, in ordinal order. */
function itemLightList(lights, item) {
    const list = [];
    const taken = item.takenLights && new Set(item.takenLights);
    for (const [ordinal, light] of lights) {
        if (!itemTakesLight(light, ordinal, item, taken))
            continue;
        const shadowMask = light.shadowItemCullMask;
        list.push({ ordinal, unshadowed: shadowMask !== null && (shadowMask & item.lightMask) === 0 });
    }
    return list.sort((a, b) => a.ordinal - b.ordinal);
}
/** A canonical string for a list, so two items with the same list share a buffer. */
function lightListId(list) {
    return list.map((entry) => `${entry.ordinal}${entry.unshadowed ? 'u' : 's'}`).join(',');
}
/** The list as a string, once per list: the lists of one frame are shared between items. */
const takenLightsId = perObject((takenLights) => takenLights.join(','));
/** A string for a placement, for use as a Map key. */
export function placementId(item) {
    const taken = item.takenLights ? takenLightsId(item.takenLights) : '*';
    return `${item.lightMask}|${item.z}|${item.layer}|${taken}`;
}
/** Whether `entry`'s light adds a `shadow_color` on its list: it tints, and shadows the list. */
function entryTintsShadow(lights, entry) {
    return !entry.unshadowed && lights.get(entry.ordinal).tintsShadow;
}
/**
 * `entries` in the order `canvas.glsl` applies them: the directional lights by slot (`:727`), then
 * the positional lights by sequence (`:768`).
 */
function applicationOrder(lights, entries) {
    const byOrdinal = new Map(entries.map((entry) => [entry.ordinal, entry]));
    return [...directionalListOrder(lights), ...positionalListOrder(lights)]
        .map((ordinal) => byOrdinal.get(ordinal))
        .filter((entry) => entry !== undefined);
}
function apartLightPlan(lights, entries) {
    const ordered = applicationOrder(lights, entries);
    let tintLayerCount = 0;
    const tintLayers = ordered.map((entry) => (entryTintsShadow(lights, entry) ? tintLayerCount++ : -1));
    return { entries: ordered, tintLayers, tintLayerCount };
}
/** The lists the canvas's lit placements take, one plan per distinct list, empty lists left out. */
export function planLightLists(lights, placements) {
    const lists = new Map();
    for (const [id, { placement, reads }] of placements) {
        const entries = itemLightList(lights, placement);
        if (entries.length === 0)
            continue;
        const listId = lightListId(entries);
        const list = lists.get(listId) ?? { entries, placementIds: [], reads: new Set() };
        list.placementIds.push(id);
        for (const read of reads)
            list.reads.add(read);
        lists.set(listId, list);
    }
    return [...lists].map(([id, { entries, placementIds, reads }]) => ({
        id,
        entries,
        placementIds,
        reads,
        tintsShadow: entries.some((entry) => entryTintsShadow(lights, entry)),
        lightsApart: reads.has('apart') ? apartLightPlan(lights, entries) : null,
    }));
}
/** The entry a role draws for, by its `unshadowed` flag, and the passes it draws in. */
const ROLE_PASSES = {
    volume: { unshadowed: false, kinds: ['light', 'tint'] },
    lit: { unshadowed: false, kinds: ['light'] },
    shade: { unshadowed: false, kinds: ['light'] },
    tint: { unshadowed: false, kinds: ['tint'] },
    fade: { unshadowed: false, kinds: ['tint'] },
    unshadowed: { unshadowed: true, kinds: ['light'] },
    unshadowedFade: { unshadowed: true, kinds: ['tint'] },
};
/** Whether a light's mesh of `role` draws in a `kind` pass over a list holding `entry`. */
export function drawsInPass(entry, role, kind) {
    if (entry === undefined)
        return false;
    const passes = ROLE_PASSES[role];
    return passes.unshadowed === entry.unshadowed && passes.kinds.includes(kind);
}
//# sourceMappingURL=itemLightList.js.map